// Edit layer style rule

import { Accordion, AccordionDetails, AccordionSummary, Button, Checkbox, IconButton, Slider, Stack, Tooltip, Typography } from "@mui/material";
import { theme_bgColorLight, theme_errorRed, theme_limeGreen, theme_orange, theme_textColorBlended, theme_textColorMain } from "../../../Theme";
import { ILayer } from '../../../Layers/LayerInterfaces';
import { CustomTextField } from "../EditLayer";
import { ChangeEvent, useState } from "react";
import { ZOOM_RANGE_SLIDER_MARKS } from "./ZoomSliderMarks";
import { FriendlyNumber } from "../../../Globals";
import CancelIcon from '@mui/icons-material/Cancel';
import EditIcon from '@mui/icons-material/Edit';
import InfoIcon from '@mui/icons-material/Info';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import TypographyWithAutoTooltip from "../../../Components/TypograpyWithAutoTooltip";
import { GetVectorLayerAttributeByID } from "../../../AttributeOps";
import { LayerAttribExpressionEditor } from "./LayerAttribExpressionEditor";
import { ILayerAttribExpression } from "./LayerAttribExpressionInterfaces";
import { EditLayerStyleRuleGeom, LINE_HALO_COLOR_DEFAULT, LINE_HALO_OPACITY_DEFAULT, LINE_HALO_THICKNESS_DEFAULT, LINE_STYLE_DEFAULT, LINE_THICKNESS_DEFAULT, POINT_HALO_COLOR_DEFAULT, POINT_HALO_OPACITY_DEFAULT, POINT_HALO_SIZE_DEFAULT, POINT_OPACITY_DEFAULT, POINT_SIZE_DEFAULT, POINT_TYPE_DEFAULT, POLY_FILL_OPACITY_DEFAULT, POLY_FILL_PATTERN_COLOR_DEFAULT, POLY_FILL_PATTERN_DEFAULT, POLY_FILL_PATTERN_OPACITY_DEFAULT, POLY_FILL_PATTERN_SPACING_DEFAULT, POLY_FILL_PATTERN_STROKE_WIDTH_DEFAULT, ValidateLayerStyleRuleGeom } from "./EditLayerStyleRuleGeom";
import { ILayerStyle, ILayerStyleRule, ILayerStyleRule_Geometry, ILayerStyleRule_TextLabel, TLayerStyleShapeType } from "./LayerStyleInterfaces";
import { SINGLE_COLOR_DEFAULT } from "./EditLayerStyle_ColorScheme_SingleColor";
import { EditLayerStyleRuleTextLabel, LAYER_STYLE_TEXT_LABEL_FONT_FAMILY_DEFAULT, LAYER_STYLE_EXT_LABEL_FONT_SIZE_DEFAULT, LAYER_STYLE_TEXT_LABEL_BOLD_DEFAULT, LAYER_STYLE_TEXT_LABEL_OPACITY_DEFAULT, LAYER_STYLE_TEXT_LABEL_HALO_SIZE_DEFAULT, LAYER_STYLE_TEXT_LABEL_FONT_COLOR_DEFAULT, LAYER_STYLE_TEXT_LABEL_HALO_COLOR_DEFAULT, LAYER_STYLE_TEXT_LABEL_HALO_OPACITY_DEFAULT, LAYER_STYLE_TEXT_LABEL_SEPARATOR_DEFAULT, LAYER_STYLE_TEXT_LABEL_WORD_WRAP_WIDTH_DEFAULT, LAYER_STYLE_TEXT_LABEL_SINGLE_CENTER_DEFAULT, LAYER_STYLE_TEXT_LABEL_SPECIAL_ATTRIBUTE_ID_USE_DEFAULT, LAYER_STYLE_TEXT_LABEL_SPECIAL_ATTRIBUTE_ID_NONE, ValidateLayerStyleRuleTextLabel } from "./EditLayerStyleRuleTextLabel";
import RestartAltIcon from '@mui/icons-material/RestartAlt';
import { LAYER_STYLE_EDITOR_SPECIAL_DEFAULT_RULE_ID } from "./EditLayerStyleEditor";


export const ZOOM_RANGE_SLIDER_MIN_VALUE = 1;
export const ZOOM_RANGE_SLIDER_MAX_VALUE = 20;
const VISIBLE_ZOOM_RANGE_SLIDER_MIN_DISTANCE = 2;



//-------------------------------------------------------------------------------
// Component props
//-------------------------------------------------------------------------------
export interface EditLayerStyleRuleProps
{
  layer?: ILayer;
  layer_shape_type: TLayerStyleShapeType;
  rule: ILayerStyleRule;   // input
  default_rule: ILayerStyleRule;
  OnRuleUpdated: any;      // output
  OnExpandRule: any;
}

//-------------------------------------------------------------------------------
// Edit layer style rule
//-------------------------------------------------------------------------------
export function EditLayerStyleRule(props: EditLayerStyleRuleProps) 
{


  const [showAttribExpressionEditor, setShowAttribExpressionEditor] = useState<boolean>(false);






  //-------------------------------------------------------------------------------
  // The style's description has changed.
  //-------------------------------------------------------------------------------
  function OnRuleDescriptionChanged(event: ChangeEvent<HTMLInputElement | HTMLTextAreaElement>): void 
  {
    if(!props.rule) return;

    const newValueStr: string = event.target.value;

    const updatedRule: ILayerStyleRule =
    {
      ...props.rule,
      description: newValueStr
    }

    // Notify caller of the change
    props.OnRuleUpdated(updatedRule)
  }

  //-------------------------------------------------------------------------------
  // Handles clamping values for the zoom range sliders (for both the geom slider and text label slider).
  //
  // It forces the 2 sliders to never overlap and have a certain distance between them
  // as they are dragged around.
  //-------------------------------------------------------------------------------
  function GetZoomRangeSliderClampedValues(value_0: number, value_1: number, activeThumb: number): number[]
  {
    if (value_1 - value_0 < VISIBLE_ZOOM_RANGE_SLIDER_MIN_DISTANCE) 
    {
      if (activeThumb === 0) 
      {
        const clamped = Math.min(value_0, ZOOM_RANGE_SLIDER_MAX_VALUE - VISIBLE_ZOOM_RANGE_SLIDER_MIN_DISTANCE);
        return [clamped, clamped + VISIBLE_ZOOM_RANGE_SLIDER_MIN_DISTANCE];
      } 
      else 
      {
        const clamped = Math.max(value_1, ZOOM_RANGE_SLIDER_MIN_VALUE + VISIBLE_ZOOM_RANGE_SLIDER_MIN_DISTANCE);
        return [clamped - VISIBLE_ZOOM_RANGE_SLIDER_MIN_DISTANCE, clamped];
      }
    } 
    else
      return [value_0, value_1];
  }

  //-------------------------------------------------------------------------------
  // The zoom range slider has changed.
  //-------------------------------------------------------------------------------
  const OnZoomRangeSliderChanged = (event: Event, newValue: number | number[], activeThumb: number) => 
  {
    if (!Array.isArray(newValue)) return;

    const min_max_scale_values: number[] = GetZoomRangeSliderClampedValues(newValue[0], newValue[1], activeThumb);
    const min_scale = min_max_scale_values[0];
    const max_scale = min_max_scale_values[1];

    // We also need to compute the scaled values (the values that will go into the
    // Geoserver <MinScaleDenominator> and <MaxScaleDenominator> tags).
    //
    // If the MIN is all the way to the left (=1), then set the scaled value to undefined (meaning no min).
    // If the MAX is all the way to the right (=20), then set the scaled value to undefined (meaning no max).

    const min_scale_denominator: number|undefined = min_scale === ZOOM_RANGE_SLIDER_MIN_VALUE ? undefined : ZOOM_RANGE_SLIDER_MARKS[min_scale-1].scaledValue;
    const max_scale_denominator: number|undefined = max_scale === ZOOM_RANGE_SLIDER_MAX_VALUE ? undefined : ZOOM_RANGE_SLIDER_MARKS[max_scale-1].scaledValue;

    const updatedRule: ILayerStyleRule =
    {
      ...props.rule,
      min_scale: min_scale, 
      max_scale: max_scale,
      min_scale_denominator: min_scale_denominator, 
      max_scale_denominator: max_scale_denominator
    }

    // Notify caller of the change
    props.OnRuleUpdated(updatedRule)    
  }

  //-------------------------------------------------------------------------------
  // Returns the text labels to use for the zoom range sliders.
  //-------------------------------------------------------------------------------
  function ScaleSliderLabelFormatter(num: number) 
  {
    return (
      <Stack direction='row' sx={{ alignItems: 'center' }}>
        <Typography sx={{ color: 'white', fontSize: '0.8rem' }}>
          Scale
        </Typography>
        <Typography sx={{ ml: 0.6, fontWeight: 'bold', color: theme_orange, fontSize: '0.9rem' }}>
          1
        </Typography>
        <Typography sx={{ fontWeight: 'bold', color: 'white', opacity: 0.8, mx: 0.2, fontSize: '0.8rem' }}>
          :
        </Typography>
        <Typography sx={{ color: theme_orange, fontWeight: 'bold', fontSize: '0.9rem' }}>
          {FriendlyNumber(ZOOM_RANGE_SLIDER_MARKS[num-1].scaledValue)}
        </Typography>
      </Stack>
    );
  }

  //-------------------------------------------------------------------------------
  // Renders a friendly version of the specified AOI attribute expression.
  //-------------------------------------------------------------------------------
  function RenderAttribFilter(filter: ILayerAttribExpression | undefined, opacity: number)
  {
    if(!props.rule) return null;

    if(!filter || !filter.clauses || filter.clauses.length === 0)
      return (
        <Typography sx={{ ml: 2, color: theme_orange, opacity: 0.6, fontSize: '0.9rem', fontWeight: 'normal' }}>
          No Filter Set
        </Typography>
      )

    return (

      <Stack direction='row' sx={{ ml: 1, alignItems: 'center', bgcolor: theme_bgColorLight+'30', 
                                   borderRadius: 2.5, px: 0.6, py: 0.6, overflow: 'auto', opacity: opacity }}>

        {filter.clauses.map(function(clause, index)
        {
          const clauseAoiAttrib = GetVectorLayerAttributeByID(props.layer, clause.attribute_id);
          if(!clauseAoiAttrib) return null;

          return (

            <Stack key={clause.id} direction='row' sx={{ alignItems: 'center' }}>

              {/* Clause connector (AND/OR) */}

              {index > 0
                ?
                  <Typography sx={{ mx: 0.7, color: theme_errorRed, opacity: 0.6, fontSize: '0.8rem', fontWeight: 'normal' }}>
                    {props.rule.filter?.operator.toUpperCase()}
                  </Typography>
                :null
              }

              {/* Search term */}

              <Stack direction='row' sx={{ mx: 0, alignItems: 'center', bgcolor: theme_textColorBlended+'20', 
                                           borderRadius: 2, px: 0.7, py: 0, boxShadow: 2 }}>

                <Typography noWrap sx={{ color: theme_textColorMain, opacity: 0.7, fontSize: '0.9rem', fontWeight: 'normal' }}>
                  {clauseAoiAttrib.display_name}
                </Typography>

                <Typography noWrap sx={{ ml: 0.5, color: theme_orange, opacity: 0.8, fontSize: '0.7rem', fontWeight: 'normal' }}>
                  {clause.operator}
                </Typography>

                <TypographyWithAutoTooltip noWrap placement='bottom' arrow
                                          sx={{ maxWidth: '180px', ml: 0.5, color: theme_limeGreen, opacity: 1, fontSize: '0.9rem', fontWeight: 'normal' }}>
                  {clause.value}
                </TypographyWithAutoTooltip>

                {clause.operator === 'is in range' || clause.operator === 'is not in range'
                  ?
                    <Stack direction='row' sx={{ alignItems: 'center' }}>
                      <Typography sx={{ mx: 0.4, color: theme_orange, opacity: 0.8, fontSize: '0.7rem', fontWeight: 'normal' }}>
                        to
                      </Typography>
                      <Typography noWrap sx={{ color: theme_limeGreen, opacity: 1, fontSize: '0.9rem', fontWeight: 'normal' }}>
                        {clause.value_max}
                      </Typography>
                    </Stack>
                  :null
                }

              </Stack>

            </Stack>
          )
        })}

      </Stack>
    )
  }

  //-------------------------------------------------------------------------------
  // Clear Filter button was pushed.
  //-------------------------------------------------------------------------------
  function OnClearFilter()
  {
    if(!props.rule) return;

    const updatedRule: ILayerStyleRule =
    {
      ...props.rule,
      filter: 
      {
        // A filter with no clauses means "override the default filter, but don't set a local filter"
        clauses: [],
        operator: "and"
      }
    }

    // Notify caller of the change
    props.OnRuleUpdated(updatedRule); 
  }

  //-------------------------------------------------------------------------------
  // The edit filter button was clicked.
  //-------------------------------------------------------------------------------
  function OnEditFilter()
  {
    setShowAttribExpressionEditor(true);
  }

  //-------------------------------------------------------------------------------
  // Call when the rule's expression has changed.
  //-------------------------------------------------------------------------------
  function OnExpressionUpdated(updatedExpression: ILayerAttribExpression)
  {
    if(!props.rule) return;

    const updatedRule: ILayerStyleRule =
    {
      ...props.rule,
      filter: updatedExpression
    }

    // Notify caller of the change
    props.OnRuleUpdated(updatedRule);
  }

  //-------------------------------------------------------------------------------
  // Call when the rule's geometry section has changed.
  //-------------------------------------------------------------------------------
  function OnRuleGeomUpdated(updatedRuleGeom: ILayerStyleRule_Geometry)
  {
    if(!props.rule) return;

    const updatedRule: ILayerStyleRule =
    {
      ...props.rule,
      geometry: updatedRuleGeom
    }

    // Notify caller of the change
    props.OnRuleUpdated(updatedRule);
  }

  //-------------------------------------------------------------------------------
  // Call when the rule's text label has changed.
  //-------------------------------------------------------------------------------
  function OnRuleTextLabelUpdated(updatedRuleTextLabel: ILayerStyleRule_TextLabel)
  {
    if(!props.rule) return;

    const updatedRule: ILayerStyleRule =
    {
      ...props.rule,
      text_label: updatedRuleTextLabel
    }

    // Notify caller of the change
    props.OnRuleUpdated(updatedRule);
  }

  //-------------------------------------------------------------------------------
  // A rule's Geom section on/off checkbox was changed.
  // 
  // NOTE: We use onClick instead of onChange because onClick allows us to stop
  //       propagation of the mouse click event (otherwise it would trigger the accordion).  
  //-------------------------------------------------------------------------------
  const OnRuleGeomCheckBoxClicked = (event: React.MouseEvent<HTMLButtonElement, MouseEvent>) => 
  {
    if(!props.rule || !props.layer) return;

    // Prevent click-through (which would trigger the accordion open/close)
    event.preventDefault();
    event.stopPropagation();

    // If this is a "Default Rule", we initialize with some actual default values.
    // If this is a regular rule, everything is set up to auto-inherit from the special "Default Rule".
    const defaultStyle: ILayerStyleRule_Geometry = props.rule.id===props.default_rule.id ? GetDefaultLayerStyle_RuleGeom(props.layer_shape_type) : GetDefaultLayerStyle_RuleGeom_Empty(props.layer_shape_type);

    const updatedRule: ILayerStyleRule =
    {
      ...props.rule,
      geometry: props.rule.geometry === undefined ? defaultStyle : undefined
    }

    // Notify caller of update
    props.OnRuleUpdated(updatedRule);
  }

  //-------------------------------------------------------------------------------
  // A rule's Text Label section on/off checkbox was changed.
  // 
  // NOTE: We use onClick instead of onChange because onClick allows us to stop
  //       propagation of the mouse click event (otherwise it would trigger the accordion).  
  //-------------------------------------------------------------------------------
  const OnRuleTextLabelCheckBoxClicked = (event: React.MouseEvent<HTMLButtonElement, MouseEvent>) => 
  {
    if(!props.rule) return;

    // Prevent click-through (which would trigger the accordion open/close)
    event.preventDefault();
    event.stopPropagation();

    // If this is a "Default Rule", we initialize with some actual default values.
    // If this is a regular rule, everything is set up to auto-inherit from the special "Default Rule".
    const defaultStyle: ILayerStyleRule_TextLabel = props.rule.id===props.default_rule.id ? GetDefaultLayerStyle_RuleTextLabel() : GetDefaultLayerStyle_RuleTextLabel_Empty();

    const updatedRule: ILayerStyleRule =
    {
      ...props.rule,
      text_label: props.rule.text_label === undefined ? defaultStyle : undefined
    }

    // Notify caller of update
    props.OnRuleUpdated(updatedRule);
  }

  //-------------------------------------------------------------------------------
  // Switch to inheriting the zoom range values from the special Defaults Rule.
  //-------------------------------------------------------------------------------
  function OnSwitchZoomRangeToUseDefault()
  {
    const updatedRule: ILayerStyleRule =
    {
      ...props.rule,
      min_scale: undefined,
      max_scale: undefined,
      min_scale_denominator: undefined,
      max_scale_denominator: undefined,
    }

    // Notify caller of update
    props.OnRuleUpdated(updatedRule);
  }

  //-------------------------------------------------------------------------------
  // Switch to inheriting the filter from the special Defaults Rule.
  //-------------------------------------------------------------------------------
  function OnSwitchFilterToUseDefault()
  {
    const updatedRule: ILayerStyleRule =
    {
      ...props.rule,
      filter: undefined,
    }

    // Notify caller of update
    props.OnRuleUpdated(updatedRule);
  }

  //-------------------------------------------------------------------------------
  // Reinitialize all values in this rule.
  //-------------------------------------------------------------------------------
  function OnResetRule()
  {
    const is_default_rule: boolean = props.rule.id === props.default_rule.id;
    let updatedRule: ILayerStyleRule;

    if(is_default_rule) // Default rule - reset with actual initial values
      updatedRule = GetDefaultLayerStyle_DefaultRule(props.layer_shape_type);
    else  // Regular rule - reset all to "inherit from default" state
      updatedRule = GetDefaultLayerStyle_RegularRule_KeepID(props.rule.id, props.layer_shape_type);

    // Notify caller of update
    props.OnRuleUpdated(updatedRule);
  }








  

  if(!props.rule) return null;


  // Main render

  return (

    <Stack sx={{ ml: 2, my: 1, p: 1, bgcolor: props.rule.id===props.default_rule.id ? '#505050'+'A0' : theme_textColorBlended+'10', borderRadius: 2 }}>

      {props.rule.id === props.default_rule.id
        ?
          <Typography sx={{ color: theme_textColorMain+'A0', fontSize: '0.8rem' }}>
            This section allows you to set up default values which can be passed down to all rules of this style.  It lets you set up these values only once here, then have all your rules use them.  Within each rule you can still overide these defaults if needed.
          </Typography>
        :null
      }

      <Stack sx={{ width: '100%', alignItems: 'end' }}>
        <Button size='small' variant="outlined" startIcon={<RestartAltIcon sx={{ width: '14px' }}/>} onClick={(_)=>OnResetRule()}
                sx={{ color: theme_textColorBlended, opacity: 0.8, fontSize: '0.6rem', textTransform: 'none', px: '6px', py: 0 }}>
          Reset All
        </Button>
      </Stack>

      <Stack direction='row' justifyContent='space-between' alignItems='center'>

        {/* Rule description */}
        {props.rule.id!==props.default_rule.id
          ?
            <CustomTextField variant='standard' size='small' autoComplete='off'
                             value={props.rule.description}
                             onChange={OnRuleDescriptionChanged}
                             label={<Typography sx={{fontSize:'0.8rem', color: theme_textColorBlended+'B0'}}>Rule Description</Typography>} 
                             sx={{ p: 0, mb:1, width: '100%' }}/>
          :null
        }

      </Stack>

      {/* Attribute filter controls */}

      <Stack sx={{ mt: 2 }}>

        <Stack direction='row' sx={{ alignItems: 'center' }}>
          <Typography sx={{ color: theme_textColorBlended, fontSize: '1.0rem' }}>
            Attribute Filter:
          </Typography>
          <Tooltip title="You can set an attribute-based filter to restrict when this rule applies.">
            <InfoIcon fontSize="small" sx={{ ml: 1, opacity: 0.7, width: '16px', height: '16px', color: theme_textColorBlended }}/>
          </Tooltip>
          <Button size='small' variant="outlined" startIcon={<EditIcon/>} onClick={(_)=>OnEditFilter()}
                  sx={{ ml: 2, height: '24px', color: theme_textColorBlended, textTransform: 'none' }}>
            Edit
          </Button>

          {/* If this is NOT the Default Rule, show the inherited/default state.  If a value is set, offer a button to switch to default */}

          {props.rule.id !== props.default_rule.id
            ?
              props.rule.filter===undefined
                ?
                  <Typography sx={{ ml: 3, fontSize: '0.7rem', color: theme_textColorMain+'70' }}>
                    Using the default filter
                  </Typography>
                : // Have local value range - show a button to switch to inherited/default value range
                  <Button variant='outlined' onClick={()=>OnSwitchFilterToUseDefault()}
                          sx={{ ml: 3, textTransform: 'none', fontSize: '0.7rem', px: 1, py: 0, opacity: 0.7 }}>
                    Use the default filter
                  </Button>
            :null
          }

        </Stack>

        <Stack direction='row' sx={{ mt: '5px', alignItems: 'center' }}>

          {props.rule.filter
            ?
              RenderAttribFilter(props.rule.filter, 1.0)
            :
              RenderAttribFilter(props.default_rule.filter, 0.5)
          }

          {/* Clear filter button */}

          {(props.rule.filter && props.rule.filter.clauses && props.rule.filter.clauses.length > 0) ||
           (props.default_rule.filter && props.default_rule.filter.clauses && props.default_rule.filter.clauses.length > 0)
            ?
              <Tooltip title='Clear filter' placement='top' arrow >
                <IconButton sx={{ p: 0.3, ml: 0.2 }} onClick={(_)=>OnClearFilter()}>
                  <CancelIcon sx={{ color: theme_textColorBlended, opacity: 0.7, p: 0.4 }}/>
                </IconButton>
              </Tooltip>
            :null
          }

        </Stack>
      </Stack>

      {/* Visible Zoom Range slider */}

      <Stack sx={{ mt: 2 }}>

        <Stack direction='row' sx={{ alignItems: 'center' }}>
          <Typography sx={{ color: theme_textColorBlended, fontSize: '1.0rem' }}>
            Visible Zoom Range
          </Typography>
          <Tooltip title="This slider allows you to restrict at what zoom levels this rule appears.  It is useful to optimize performance for layers with a lot of geometry objects (too many geometry objects visible at once can result in slow map performance).">
            <InfoIcon fontSize="small" sx={{ ml: 1, opacity: 0.7, width: '16px', height: '16px', color: theme_textColorBlended }}/>
          </Tooltip>

          {/* If this is NOT the Default Rule, show the inherited/default state.  If a value is set, offer a button to switch to default */}

          {props.rule.id !== props.default_rule.id
            ?
              (props.rule.min_scale===undefined || props.rule.max_scale===undefined) && props.default_rule.min_scale && props.default_rule.max_scale
                ?
                  <Stack direction='row' sx={{ alignItems: 'center' }}>
                    <Typography sx={{ ml: 3, fontSize: '0.7rem', color: theme_textColorMain+'70' }}>
                      Default:
                    </Typography>
                    <Typography sx={{ ml: 0.5, fontSize: '0.7rem', color: theme_orange+'A0' }}>
                      1:{FriendlyNumber(ZOOM_RANGE_SLIDER_MARKS[props.default_rule.min_scale-1].scaledValue)}
                    </Typography>           
                    <Typography sx={{ mx: 0.5, fontSize: '0.7rem', color: theme_textColorMain+'70' }}>
                      to
                    </Typography>
                    <Typography sx={{ fontSize: '0.7rem', color: theme_orange+'A0' }}>
                      1:{FriendlyNumber(ZOOM_RANGE_SLIDER_MARKS[props.default_rule.max_scale-1].scaledValue)}
                    </Typography>           
                  </Stack>
                : // Have local value range - show a button to switch to inherited/default value range
                  <Button variant='outlined' onClick={()=>OnSwitchZoomRangeToUseDefault()}
                          sx={{ ml: 3, textTransform: 'none', fontSize: '0.7rem', px: 1, py: 0, opacity: 0.7 }}>
                    Use the default zoom range
                  </Button>
            :null
          }

        </Stack>

        <Stack sx={{ width: '100%', alignItems: 'center' }}>
          <Stack sx={{ width: '80%', justifyContent: 'center', alignItems: 'center' }}>

            {/* Slider - show local value range */}

            {props.rule.min_scale!==undefined && props.rule.max_scale!==undefined
              ? 
                <Slider disableSwap min={ZOOM_RANGE_SLIDER_MIN_VALUE} max={ZOOM_RANGE_SLIDER_MAX_VALUE} step={1}
                        value={[props.rule.min_scale, props.rule.max_scale]}
                        onChange={OnZoomRangeSliderChanged}
                        valueLabelFormat={ScaleSliderLabelFormatter} valueLabelDisplay='auto'
                        marks={ZOOM_RANGE_SLIDER_MARKS}
                        sx={{ color: '#889645', width: '100%',
                              // this Hides the tick labels (too crowded and not needed)
                              '& .MuiSlider-markLabel': { color: 'transparent' }
                          }}/>
              :
                props.default_rule.min_scale!==undefined && props.default_rule.max_scale!==undefined 
                  ?
                    // Slider - no local value range - show inherited/default value range
                    <Slider disableSwap min={ZOOM_RANGE_SLIDER_MIN_VALUE} max={ZOOM_RANGE_SLIDER_MAX_VALUE} step={1}
                            value={[props.default_rule.min_scale, props.default_rule.max_scale]}
                            onChange={OnZoomRangeSliderChanged}
                            valueLabelFormat={ScaleSliderLabelFormatter} valueLabelDisplay='auto'
                            marks={ZOOM_RANGE_SLIDER_MARKS}
                            sx={{ color: '#889645', opacity: 0.4, width: '100%',
                                  // this Hides the tick labels (too crowded and not needed)
                                  '& .MuiSlider-markLabel': { color: 'transparent' }
                              }}/>
                  :null
            }

            <Stack direction='row' sx={{ width: '100%', mt: -2, justifyContent: 'space-between' }}>
              <Stack direction='column' sx={{ alignItems: 'center', ml: -5 }}>
                <Typography sx={{ color: theme_orange, opacity: 0.7, fontSize: '0.8rem' }}>
                  Fully Zoomed IN
                </Typography>
                <Typography sx={{ color: theme_orange, opacity: 0.5, fontSize: '0.7rem' }}>
                  (street level)
                </Typography>
              </Stack>
              <Stack direction='column' sx={{ alignItems: 'center', mr: -7 }}>
                <Typography sx={{ color: theme_orange, opacity: 0.7, fontSize: '0.8rem' }}>
                  Fully Zoomed OUT
                </Typography>
                <Typography sx={{ color: theme_orange, opacity: 0.5, fontSize: '0.7rem' }}>
                  (continental US)
                </Typography>
              </Stack>
            </Stack>

          </Stack>
        </Stack>

      </Stack>
    
      {/* Rule: Geometry */}

      <Accordion expanded={props.rule.geometry ? true : false} disableGutters={true}
                 sx={{ my: 0.5, mt: 2, width: '100%', boxShadow: 0, bgcolor: 'transparent', p: 0 }} 
                 //onChange={(e,expanded) => OnRuleGeomAccordionExpandChange(e,expanded,rule)}
                 >

            <AccordionSummary expandIcon={<ExpandMoreIcon sx={{ opacity: 1, height: '1rem', p: 0 }} />}
                              sx={{ width: '100%', p: 0, bgcolor: '#534582', borderRadius: 2, my: '4px', height: '20px' }}>
              <Stack direction='row' sx={{ mx: 2, width: '100%', alignItems: 'center' }}>

                {props.rule.id!==props.default_rule.id
                  ? // The rule ON/OFF checkbox is hidden for the special 'Default Rule'
                    <Checkbox size='medium' checked={props.rule.geometry ? true : false}
                              onClick={(e)=>OnRuleGeomCheckBoxClicked(e)}
                              sx=
                              {{ 
                                ml: '-12px', mr: '2px', color: theme_textColorBlended, opacity: 0.8, width: '35px', height: '35px',
                                '&.Mui-checked': { color: theme_textColorBlended } 
                              }} />
                  :null
                }

                <Typography sx={{ width: '100%', color: theme_limeGreen+'C0', fontSize: '0.9rem' }}>
                  Geometry
                </Typography>

              </Stack>

            </AccordionSummary>
            
            <AccordionDetails sx={{ bgcolor: 'transparent', p:0 }}>

              <EditLayerStyleRuleGeom rule={props.rule} OnRuleGeomUpdated={OnRuleGeomUpdated} 
                                      defaultRule={props.default_rule}
                                      is_default_rule={props.rule.id===props.default_rule.id}
                                      layer_shape_type={props.layer_shape_type} layer={props.layer}
                                      OnExpandRule={props.OnExpandRule}/>

            </AccordionDetails>

        </Accordion>

      {/* Rule: Text Label */}

      <Accordion expanded={props.rule.text_label ? true : false} disableGutters={true}
              sx={{ my: 0.5, width: '100%', boxShadow: 0, bgcolor: 'transparent', p: 0 }} 
              //onChange={(e,expanded) => OnRuleGeomAccordionExpandChange(e,expanded,rule)}
              >

            <AccordionSummary expandIcon={<ExpandMoreIcon sx={{ opacity: 1, height: '1rem', p: 0 }} />}
                              sx={{ width: '100%', p: 0, bgcolor: '#1F6B73', borderRadius: 2, my: '4px', height: '20px' }}>
              <Stack direction='row' sx={{ mx: 2, width: '100%', alignItems: 'center' }}>

                {props.rule.id!==props.default_rule.id
                  ? // The rule ON/OFF checkbox is hidden for the special 'Default Rule'
                    <Checkbox size='medium' checked={props.rule.text_label ? true : false}
                              onClick={(e)=>OnRuleTextLabelCheckBoxClicked(e)}
                              sx=
                              {{ 
                                ml: '-12px', mr: '2px', color: theme_textColorBlended, opacity: 0.8, width: '35px', height: '35px',
                                '&.Mui-checked': { color: theme_textColorBlended } 
                              }} />
                  :null
                }
                
                <Typography sx={{ width: '100%', color: theme_limeGreen+'C0', fontSize: '0.9rem' }}>
                  Text Label
                </Typography>

              </Stack>

            </AccordionSummary>
            
            <AccordionDetails sx={{ bgcolor: 'transparent', p:0 }}>

              <EditLayerStyleRuleTextLabel ruleTextLabel={props.rule.text_label} defaultRuleTextLabel={props.default_rule.text_label}
                                           is_default_rule={props.rule.id===props.default_rule.id}
                                           OnRuleTextLabelUpdated={OnRuleTextLabelUpdated} layer={props.layer}/>

            </AccordionDetails>

        </Accordion>






        {/* Layer attribute expression editor dialog window */}

        <LayerAttribExpressionEditor showExpressionEditor={showAttribExpressionEditor}
                                     setShowExpressionEditor={setShowAttribExpressionEditor}
                                     layer={props.layer} 
                                     expression={props.rule.filter} 
                                     OnExpressionChanged={OnExpressionUpdated}/>


    </Stack>
  )
}



//-------------------------------------------------------------------------------
// Returns a new default rule.
//-------------------------------------------------------------------------------
export function GetDefaultLayerStyle_DefaultRule(layer_shape_type: TLayerStyleShapeType): ILayerStyleRule
{
  const newRule: ILayerStyleRule = 
  {
    id: LAYER_STYLE_EDITOR_SPECIAL_DEFAULT_RULE_ID,
    enabled: true,
    description: "",
    filter: undefined,
    min_scale: ZOOM_RANGE_SLIDER_MIN_VALUE,
    max_scale: ZOOM_RANGE_SLIDER_MAX_VALUE,
    geometry: GetDefaultLayerStyle_RuleGeom(layer_shape_type),
    text_label: GetDefaultLayerStyle_RuleTextLabel(),
  };

  return newRule;
}

//-------------------------------------------------------------------------------
// Returns a new default rules.
// NOTE: The ID is specified (this is used to reset an existing rule, but keep its original id)
//-------------------------------------------------------------------------------
export function GetDefaultLayerStyle_RegularRule_KeepID(id: number, shape_type: TLayerStyleShapeType): ILayerStyleRule
{
  const newRule: ILayerStyleRule = 
  {
    id: id,
    enabled: true,
    description: "",
    filter: undefined,
    min_scale: undefined,
    max_scale: undefined,
    min_scale_denominator: undefined,
    max_scale_denominator: undefined,
    geometry: GetDefaultLayerStyle_RuleGeom_Empty(shape_type),
    text_label: undefined,
  }

  return newRule;
}

//-------------------------------------------------------------------------------
// Returns a new default rules.
// NOTE: The "existing" array is used to auto-determine the next id.
//-------------------------------------------------------------------------------
export function GetDefaultLayerStyle_RegularRule(existingRules: ILayerStyleRule[], shape_type: TLayerStyleShapeType): ILayerStyleRule
{
  // Auto-determine the next id
  let next_id: number = existingRules.length === 0 ? 1 : existingRules.reduce((prev, current) => (prev.id > current.id) ? prev : current).id + 1;

  return GetDefaultLayerStyle_RegularRule_KeepID(next_id, shape_type);
}

//-------------------------------------------------------------------------------
// This version sets everything up to inherit from the style's special "Default Rule".
// This is used when the Geom section of a regular rule is first enabled.
//-------------------------------------------------------------------------------
function GetDefaultLayerStyle_RuleGeom_Empty(shape_type: TLayerStyleShapeType): ILayerStyleRule_Geometry
{
  const newRuleGeom: ILayerStyleRule_Geometry = 
  {
    color_scheme: 'none',
    attribute_id: undefined,
    attribute_type: undefined,
    // These are only here because colors can't be set to 'undefined' due to how the MUI color component works
    point_halo_color: '',
    line_halo_color: '',
    poly_fill_pattern_color: ''
  }

  // Some values depend on the layer geometry shape type (point/line/polygon)

  if(shape_type === 'point')
  {
    newRuleGeom.point_type = 'none';
    newRuleGeom.point_halo_color = '';
  }
  else if(shape_type === 'line' || shape_type === 'polygon')
  {
    newRuleGeom.line_style = 'none';
    newRuleGeom.line_halo_color = '';

    // Polygon ONLY
    if(shape_type === 'polygon')
    {
      newRuleGeom.poly_fill_pattern = 'None';
      newRuleGeom.poly_fill_pattern_color = '';
    }
  }

  return newRuleGeom;
}

//-------------------------------------------------------------------------------
// This version actually sets up a very simple initial style.  
// This is used when initializing the Geom section of the special "Default Rule".
//-------------------------------------------------------------------------------
export function GetDefaultLayerStyle_RuleGeom(shape_type: TLayerStyleShapeType): ILayerStyleRule_Geometry
{
  const newRuleGeom: ILayerStyleRule_Geometry = 
  {
    color_scheme: 'single color',
    attribute_id: undefined,
    attribute_type: undefined,
    single_color: 
    {
      color: SINGLE_COLOR_DEFAULT
    },
    // These are only here because colors can't be set to 'undefined' due to how the MUI color component works
    point_halo_color: '',
    line_halo_color: '',
    poly_fill_pattern_color: ''
  }

  // Some values depend on the layer geometry shape type (point/line/polygon)

  if(shape_type === 'point')
  {
    newRuleGeom.point_type = POINT_TYPE_DEFAULT;
    newRuleGeom.point_size = POINT_SIZE_DEFAULT;
    newRuleGeom.point_opacity = POINT_OPACITY_DEFAULT;
    newRuleGeom.point_halo_size = POINT_HALO_SIZE_DEFAULT;
    newRuleGeom.point_halo_opacity = POINT_HALO_OPACITY_DEFAULT;
    newRuleGeom.point_halo_color = POINT_HALO_COLOR_DEFAULT;
  }
  else if(shape_type === 'line' || shape_type === 'polygon')
  {
    newRuleGeom.line_thickness = LINE_THICKNESS_DEFAULT;
    newRuleGeom.line_opacity = POINT_OPACITY_DEFAULT;
    newRuleGeom.line_style = LINE_STYLE_DEFAULT;
    newRuleGeom.line_halo_thickness = LINE_HALO_THICKNESS_DEFAULT;
    newRuleGeom.line_halo_color = LINE_HALO_COLOR_DEFAULT;
    newRuleGeom.line_halo_opacity = LINE_HALO_OPACITY_DEFAULT;

    // Polygon ONLY
    if(shape_type === 'polygon')
    {
      newRuleGeom.poly_fill_opacity = POLY_FILL_OPACITY_DEFAULT;
      newRuleGeom.poly_fill_pattern = POLY_FILL_PATTERN_DEFAULT;
      newRuleGeom.poly_fill_pattern_color = POLY_FILL_PATTERN_COLOR_DEFAULT;
      newRuleGeom.poly_fill_pattern_spacing = POLY_FILL_PATTERN_SPACING_DEFAULT;
      newRuleGeom.poly_fill_pattern_stroke_width = POLY_FILL_PATTERN_STROKE_WIDTH_DEFAULT;
      newRuleGeom.poly_fill_pattern_opacity = POLY_FILL_PATTERN_OPACITY_DEFAULT;
    }
  }

  return newRuleGeom;
}

//-------------------------------------------------------------------------------
// This version sets everything up to inherit from the style's special "Default Rule".
// This is used when the Text Label section of a regular rule is first enabled.
//-------------------------------------------------------------------------------
function GetDefaultLayerStyle_RuleTextLabel_Empty(): ILayerStyleRule_TextLabel
{
  const newRuleTextLabel: ILayerStyleRule_TextLabel = 
    {
      primary_attribute_id: LAYER_STYLE_TEXT_LABEL_SPECIAL_ATTRIBUTE_ID_USE_DEFAULT,
      secondary_attribute_id: LAYER_STYLE_TEXT_LABEL_SPECIAL_ATTRIBUTE_ID_USE_DEFAULT,
      secondary_label_separator: 'None',
      font_family: 'None',
      size: undefined,
      color: '',
      bold: undefined,
      opacity: undefined,
      halo_size: undefined,
      halo_color: '',
      halo_opacity: undefined,
      word_wrap_width: undefined,
      single_center_label: undefined,
    }

  return newRuleTextLabel;
}

//-------------------------------------------------------------------------------
// This version actually sets up a very simple initial style.  
// This is used when initializing the Text Label section of the special "Default Rule".
//-------------------------------------------------------------------------------
export function GetDefaultLayerStyle_RuleTextLabel(): ILayerStyleRule_TextLabel
{
  const newRuleTextLabel: ILayerStyleRule_TextLabel = 
    {
      primary_attribute_id: LAYER_STYLE_TEXT_LABEL_SPECIAL_ATTRIBUTE_ID_NONE,
      secondary_attribute_id: LAYER_STYLE_TEXT_LABEL_SPECIAL_ATTRIBUTE_ID_NONE,
      font_family: LAYER_STYLE_TEXT_LABEL_FONT_FAMILY_DEFAULT,
      size: LAYER_STYLE_EXT_LABEL_FONT_SIZE_DEFAULT,
      color: LAYER_STYLE_TEXT_LABEL_FONT_COLOR_DEFAULT,
      bold: LAYER_STYLE_TEXT_LABEL_BOLD_DEFAULT,
      opacity: LAYER_STYLE_TEXT_LABEL_OPACITY_DEFAULT,
      halo_size: LAYER_STYLE_TEXT_LABEL_HALO_SIZE_DEFAULT,
      halo_color: LAYER_STYLE_TEXT_LABEL_HALO_COLOR_DEFAULT,
      halo_opacity: LAYER_STYLE_TEXT_LABEL_HALO_OPACITY_DEFAULT,
      secondary_label_separator: LAYER_STYLE_TEXT_LABEL_SEPARATOR_DEFAULT,
      word_wrap_width: LAYER_STYLE_TEXT_LABEL_WORD_WRAP_WIDTH_DEFAULT,
      single_center_label: LAYER_STYLE_TEXT_LABEL_SINGLE_CENTER_DEFAULT,
    }

  return newRuleTextLabel;
}

//-------------------------------------------------------------------------------
// Validates the specified rule.
//
// Works for both regular rules and the special global defaults rule.
// 
// Returns an error message if validation FAILS, or empty string if it succeeds.
//
// Error string format:  [Style 1 Rule 1] Error message here
//   (the style and rule numbers are just simple indexes, starting at 1 - the special default rule is rule 0)
//-------------------------------------------------------------------------------
export function ValidateLayerStyleRule(layer_shape_type: TLayerStyleShapeType, rule: ILayerStyleRule, 
                                       default_rule: ILayerStyleRule, errMsgPrefix: string): string
{
  if(layer_shape_type !== 'point' && layer_shape_type !== 'line' && layer_shape_type !== 'polygon')
    return errMsgPrefix + 'Invalid layer shape type';

  if(!rule) 
    return errMsgPrefix + 'Invalid rule';

  if(rule.id === undefined || rule.id < 0) 
    return errMsgPrefix + 'Invalid rule id';

  const is_default_rule: boolean = rule.id === default_rule.id;
  
  // Filter: nothing should need validating, the filter popup UI already validates

  // Zoom scale slider: nothing should need validating, the UI is strict

  // The Global Defaults rule's geom and text label sections must always be defined
  if(default_rule.geometry === undefined)
    return errMsgPrefix + 'Invalid or missing geometry section';
  if(default_rule.text_label === undefined)
    return errMsgPrefix + 'Invalid or missing text label section';

  // For regular rules, either GEOM or TEXT LABEL sections need to be enabled
  if(is_default_rule === false && !rule.geometry && !rule.text_label)
    return errMsgPrefix + 'Either the Geometry section or the Text Label section must be defined (or both)';

  // Validate the GEOMETRY section

  let errMsg: string = ValidateLayerStyleRuleGeom(layer_shape_type, rule.geometry, default_rule.geometry, is_default_rule, errMsgPrefix + 'Geometry: ');
  if(errMsg !== '') return errMsg;

  // Validate the TEXT LABEL section

  errMsg = ValidateLayerStyleRuleTextLabel(rule.text_label, default_rule?.text_label, is_default_rule, errMsgPrefix + 'Text Label: ');
  if(errMsg !== '') return errMsg;

  // Success
  return '';
}